/**
 * StatsLedger — the third region of the dossier, and the only one that asks nothing of anybody.
 *
 * PRESENTATIONAL, END TO END. Every number, string and fraction below is `setupLedger`'s; this
 * file decides where they sit and nothing else. That is the same split the manifest keeps between
 * `computeSetupState` and `Manifest.tsx`, and it is why the ledger is testable at all — the
 * geometry is not, and the manual matrix in the plan is the instrument for that half.
 *
 * ── FOUR MODULES, IN THE OWNER'S ORDER ───────────────────────────────────────────────────────
 *
 *   1 where this contract stands   the manifest's own arithmetic, mirrored
 *   2 days in setup                the one fact no row on the page carries
 *   3 at stake                     the money — ABOVE THE FOLD, by ruling
 *   4 waiting on                   who is holding this up, and for how long
 *
 * THE COLUMN IS CHART-FREE, BY THE OWNER'S RULING. It carried a fifth module — a dated ceremony
 * strip over the documents' raw timestamps — and it is gone: the signing history is a thing an
 * admin consults deliberately on the agreements area itself, not a shape they need beside the work.
 * Nothing on this page imports a charting library any more.
 *
 * THE MONEY RISES (owner's ruling). The tail module takes the scroll: a wait list is read once,
 * while what a contract is worth is the thing an admin glances at every time they open the page.
 *
 * ── WHAT THIS COMPONENT IS FORBIDDEN ─────────────────────────────────────────────────────────
 *
 * - NO INTERACTIVE ELEMENTS. The narrator's action stays the only brand-filled button on the page
 *   and the manifest stays the only place a click navigates. A stats column with its own controls
 *   would be a second way to do the same things, and a third place for them to disagree.
 * - NO `b-`/`f-` DOM IDS. Those prefixes are unscoped `getElementById` jump targets
 *   (`focusJumpTarget`), so an id minted here could silently steal a manifest jump from the canvas
 *   block that owns it. `sdstat-` is the reserved prefix if one is ever needed for
 *   `aria-labelledby`.
 * - NO SECOND `aria-live`. `NarratorLine` owns the page's one polite region; a column that
 *   announced every recomputation would talk over the sentence that actually changed.
 * - NO `--success`, no brand fill, no percentage, no arc, and no animation on any bar or number.
 *
 * ── TWO HOMES, ONE SET OF MODULES (owner's ruling: "all sections should show irrespective of the
 *    screen width") ────────────────────────────────────────────────────────────────────────────
 *
 * The ledger renders at EVERY width. It cannot render as a THIRD COLUMN at every width — below
 * 1440 the measured ink troughs fall under the page's 52px floor and the canvas would have to give
 * up more of its measure than the work can spare — so the four modules have two forms and the CSS
 * picks one:
 *
 *   ≥ 1440   `StatsLedger` — the 222px aside, third track of `.setup-dossier`.
 *   < 1440   `LedgerSection` — the same modules as a fixed-measure grid at the FOOT OF THE CANVAS,
 *            after the area's Previous/Next foot, inside the canvas's own scrollport.
 *
 * `LedgerModules` is the shared body and the reason the two can never drift: neither home owns a
 * module, a heading or an order. Both are always mounted and the `display` rules decide which one
 * is drawn — a width test in JavaScript would re-render the page on every resize frame to answer a
 * question CSS answers for free, and four modules of text cost nothing to mount twice.
 */

import type { AgreementTokenRow } from '@/lib/agreements/api';

import { useSetupLedger } from './useSetupLedger';
import type { LedgerView } from './setupLedger';
import type { SetupSnapshot, SetupState } from '../setupState';

interface Props {
    state?: SetupState | null;
    snapshot?: SetupSnapshot | null;
    tokens?: AgreementTokenRow[] | null;
    contractCreatedAt?: string | null;
}

// ─── the column ──────────────────────────────────────────────────────────────

function StandingModule({ standing }: { standing: NonNullable<LedgerView['standing']> }) {
    return (
        <section className="sd-ledger-mod">
            <h3 className="sd-eyebrow">Where this contract stands</h3>
            <div className="sd-stand sd-num">
                <div>
                    <b>{standing.areasComplete}</b> of {standing.areaCount} areas complete
                </div>
                <div>
                    <b>{standing.needYou}</b> need you
                </div>
                <div>
                    <b>{standing.waitingOnOthers}</b> waiting on others
                </div>
            </div>
            <div className="sd-areas">
                {standing.areas.map((line) => (
                    <div key={line.area}>
                        <div className="sd-arow">
                            <span className="sd-arow-n">{line.label}</span>
                            {/*
                              * THE RECEIPT REPLACES THE COUNTER — it does not sit beside it. The
                              * manifest makes the same substitution in the same slot, and the
                              * mirror test asserts this branch against `groupCounter` itself.
                              */}
                            {line.receipt ? (
                                <span className="sd-arow-r" title={line.receipt}>
                                    {line.receipt}
                                </span>
                            ) : (
                                <span className="sd-arow-c sd-num">{line.counter}</span>
                            )}
                        </div>
                        {line.segments.length > 0 && (
                            <div className="sd-segs" aria-hidden="true">
                                {line.segments.map((filled, index) => (
                                    <span
                                        key={index}
                                        className={filled ? 'sd-seg sd-seg-on' : 'sd-seg'}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
}

function WaitMeta({ wait }: { wait: LedgerView['waiting'][number] }) {
    const parts: string[] = [];
    if (wait.elapsedDays != null) parts.push(`${wait.elapsedDays}d`);
    if (wait.openCount != null) {
        parts.push(wait.openCount === 0 ? 'not opened' : `opened ${wait.openCount}×`);
    }
    if (wait.linkExpired) parts.push('link expired');
    if (parts.length === 0) return null;
    return <span className="sd-wait-m sd-num">{parts.join(' · ')}</span>;
}

/**
 * THE FOUR MODULES, AND NEITHER HOME OWNS THEM.
 *
 * Everything below is the body both `StatsLedger` (the ≥1440 aside) and `LedgerSection` (the
 * canvas foot) render, in one order, from one view model. A module added here appears in both
 * forms by construction — which is the point: the two homes are a `display` decision in
 * `index.css`, never two renderings that have to be kept in step by hand.
 */
function LedgerModules({ ledger }: { ledger: LedgerView }) {
    return (
        <>
            {ledger.standing && <StandingModule standing={ledger.standing} />}

            {ledger.days && (
                <section className="sd-ledger-mod">
                    <h3 className="sd-eyebrow">Days in setup</h3>
                    <div className="sd-stat-fig sd-num">{ledger.days.days}</div>
                    <div className="sd-stat-lab">
                        {ledger.days.days === 1 ? 'day in setup' : 'days in setup'}
                    </div>
                    <div className="sd-stat-sub sd-num">{ledger.days.openedText}</div>
                </section>
            )}

            <section className="sd-ledger-mod">
                <h3 className="sd-eyebrow">At stake</h3>
                {ledger.stake.length === 0 ? (
                    <p className="sd-hint">Nothing is recorded yet.</p>
                ) : (
                    <div className="sd-stake">
                        {ledger.stake.map((line) => (
                            <div className="sd-stake-r" key={line.key}>
                                <span className="sd-stake-k">{line.key}</span>
                                <span className="sd-stake-v sd-num">
                                    {line.value}
                                    {line.sub && <span className="sd-stake-sub">{line.sub}</span>}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/*
              * THE MODULE NEVER VANISHES, even at zero. "Nobody is being waited on" is a fact an
              * admin wants to read on a Monday morning, and a heading that disappears when the
              * answer is good makes its absence ambiguous with its own failure to render.
              */}
            <section className="sd-ledger-mod">
                <h3 className="sd-eyebrow">Waiting on</h3>
                {ledger.waiting.length === 0 ? (
                    <p className="sd-hint">
                        <span className="sd-num">0</span> — nobody is being waited on.
                    </p>
                ) : (
                    ledger.waiting.map((wait) => (
                        <div className="sd-wait" key={wait.rowId}>
                            <div className="sd-wait-top">
                                <span className="sd-mark">
                                    <span className="sd-dot sd-dot-wait" />
                                </span>
                                <span className="sd-wait-n">{wait.subject}</span>
                                <WaitMeta wait={wait} />
                            </div>
                            {/*
                              * THE ROW'S OWN LABEL, UNDER THE NAME. One person can be holding up
                              * two things at once — the hire purchase agreement and the insurance
                              * loan agreement are two waits on one driver — and `Kofi Mensah`
                              * printed twice with two day counts is a column an admin cannot act
                              * on. The selector drops it exactly where it would repeat the
                              * subject, so an actor-less wait prints its label once and not twice.
                              */}
                            {wait.label && <div className="sd-wait-q">{wait.label}</div>}
                            {/*
                              * NO BAR WITHOUT A DAY COUNT. A zero-width bar under a wait whose
                              * link date never arrived reads as "waited no time", which is the one
                              * thing this row does not know.
                              */}
                            {wait.barFraction != null && (
                                <div className="sd-wait-track" aria-hidden="true">
                                    <div
                                        className="sd-wait-bar"
                                        style={{ width: `${(wait.barFraction * 100).toFixed(1)}%` }}
                                    />
                                </div>
                            )}
                        </div>
                    ))
                )}
            </section>
        </>
    );
}

/**
 * HOME 1 — THE ASIDE, at 1440px and above.
 *
 * The third in-flow child of `.setup-dossier`'s grid and the third scrollport on the page: a 222px
 * ink column in the right margin, `display: none` below its two `min-width` bands (and a
 * `display: none` child generates no grid item, which is what lets the same markup serve a
 * two-track grid at every narrower width).
 */
export default function StatsLedger({ state, snapshot, tokens, contractCreatedAt }: Props) {
    const ledger = useSetupLedger({ state, snapshot, tokens, contractCreatedAt });

    return (
        <aside className="sd-ledger" aria-label="Contract statistics">
            <LedgerModules ledger={ledger} />
        </aside>
    );
}

/**
 * HOME 2 — THE CANVAS FOOT, below 1440px, and the answer to "all sections should show
 * irrespective of the screen width".
 *
 * THE SAME FOUR MODULES, STACKED RATHER THAN COLUMNAR. It is the last thing in `.sd-canvas`, after
 * the area's Previous/Next foot and separated from it by the page's biggest trough
 * (`--sd-area-gap`) and no rule at all — the figures are the end of the document, not a sidebar
 * bolted under it. Between 1024 and 1439 it inherits the canvas's own scroll; below 1024 the
 * canvas is the page, so it inherits the page's.
 *
 * IT IS NOT A GRID CHILD OF `.setup-dossier` and must never become one: it lives INSIDE the canvas
 * `<main>`, so it cannot mint a fourth column or an implicit second row.
 *
 * NO IDS AND NO `aria-live`, the same two prohibitions the aside carries — `b-`/`f-` ids are the
 * page's unscoped jump targets and `NarratorLine` owns the one polite region. Two `<section
 * aria-label="Contract statistics">` landmarks exist in the markup, but never in the accessibility
 * tree at once: whichever home the width does not want is `display: none` and is not exposed.
 */
export function LedgerSection({ state, snapshot, tokens, contractCreatedAt }: Props) {
    // THE SAME PURE SELECTOR ON THE SAME INPUTS, memoised per home. Two calls cannot disagree —
    // `setupLedger` is deterministic and reads the snapshot's own clock — and the alternative,
    // hoisting the view model into the page, would put a third region's derivation in a file that
    // owns neither of its homes.
    const ledger = useSetupLedger({ state, snapshot, tokens, contractCreatedAt });

    return (
        <section className="sd-ledger-flow" aria-label="Contract statistics">
            <LedgerModules ledger={ledger} />
        </section>
    );
}
